/**
 * Enquête n°20 — Adapté de l'Étude de Cas CCS n°15
 * (Sensibiliser sur la notion de fatalité lors d'un accident.)
 */

import { AlertTriangle, Lightbulb, Quote, Users, ShieldCheck } from 'lucide-react';

export const fataliteAccidentEnquete = {
  slug: 'fatalite-accident-moto',
  themeCode: 'M',
  issueNumber: 20,
  totalIssues: 20,
  series: 'enquetes',
  category: 'Enquête facteurs humains',
  shortTitle: 'Fatalité ?',
  title: 'Le « pas de chance » : pourquoi 90 % des accidents étaient évitables',
  kicker: 'Enquête n°20 · Série « Témoignages & Sécurité »',
  subtitle:
    "« Un coup du sort », « le destin », « le mauvais moment au mauvais endroit ». C'est ce qu'on dit après. Mais l'ONISR le répète depuis 20 ans : 9 accidents motos sur 10 ont une cause comportementale identifiable. La fatalité n'existe pas — ce sont les facteurs cumulés.",
  heroImage: '/img/magazines/conducteur.jpg',
  heroImageAlt: 'Motard penseur devant sa moto — réflexion sur les facteurs d’accident',
  readTimeMin: 6,
  seoDescription:
    "Enquête sur la notion de fatalité dans les accidents moto : statistiques ONISR, modèle du fromage suisse de Reason, 4 facteurs comportementaux identifiés, comment briser la chaîne des causes.",
  seoKeywords:
    'fatalité accident moto, ONISR, modèle Reason, fromage suisse, causes accident motocycliste, facteurs humains, sensibilisation 2 roues',

  edito: {
    title: 'La fatalité, alibi pratique',
    body: 'Le mot « fatalité » arrange tout le monde. Il dédouane le pilote, calme la famille, ferme l’enquête. Mais derrière chaque accident grave de 2 roues, l’ONISR identifie en moyenne <strong>4 à 7 facteurs contributifs</strong> — vitesse inadaptée, fatigue, alcool, EPI insuffisant, météo sous-estimée, expérience limitée. Le « pas de chance » n’existe que dans les conversations de comptoir.',
    signedBy: 'La rédaction Code 2 Roues',
  },

  sections: [
    {
      type: 'lesson',
      icon: AlertTriangle,
      partLabel: 'Chapitre 1',
      title: 'Les chiffres qui contredisent le destin',
      paragraphs: [
        "<strong>22 %</strong> des tués sur la route en France sont motocyclistes, alors que les 2 roues motorisés représentent <strong>2 % du trafic</strong> (ONISR bilan 2023). Risque relatif : <strong>x 22</strong> par rapport à une voiture.",
        "<strong>40 %</strong> des accidents mortels moto = perte de contrôle en virage (vitesse inadaptée + trajectoire mal jugée). 0 % de fatalité là-dedans : ce sont des choix de pilotage.",
        "<strong>30 à 50 %</strong> des accidents moto en intersection sont des SMIDSY (« Sorry Mate I Didn't See You » — étude MAIDS). Encore une cause identifiable : visibilité et anticipation insuffisantes.",
      ],
      keyPoints: [
        '22 % des tués pour 2 % du trafic',
        '40 % en virage = vitesse + trajectoire',
        '30-50 % SMIDSY en intersection',
        '0 % de hasard pur',
      ],
},
    {
      type: 'didyouknow',
      icon: Lightbulb,
      title: 'Le modèle du « fromage suisse » (James Reason)',
      body: "Pour qu'un accident grave survienne, il faut que <strong>plusieurs trous s'alignent</strong> dans plusieurs « tranches » de protection : conducteur fatigué + vitesse élevée + virage sans visibilité + chaussée dégradée + EPI insuffisant + secours éloignés. Boucher <strong>un seul trou</strong> dans cette chaussette — ralentir, mettre un blouson normalisé, dormir 7 h — et la chaîne se brise. Voilà pourquoi l’ONISR parle de « construction d’un accident » plutôt que de « cause unique ».",
    },
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 2',
      title: 'Les 4 leviers qui marchent vraiment',
      paragraphs: [
        "<strong>1. Vitesse adaptée</strong> aux circonstances (R413-17). Réduire de 10 km/h en milieu rural diminue le risque d’accident mortel d’environ 30 % (études IFSTTAR).",
        '<strong>2. EPI complet</strong> : casque homologué, gants CE, blouson + pantalon avec coques. Réduit la gravité des blessures de 35 % en moyenne.',
        '<strong>3. Anticipation (regard à 12 secondes)</strong> + dépister les SMIDSY par regard contact. Coupe la chaîne avant le choc.',
        "<strong>4. État psychophysiologique</strong> : pas d'alcool, sommeil suffisant, pas de stupéfiants. Règle absolue : si tu doutes, tu ne pars pas.",
      ],
      keyPoints: [
        'Vitesse – 10 km/h = – 30 % mortalité',
        'EPI complet – 35 % gravité blessures',
        'Regard 12 s coupe la chaîne',
        'Fatigue/alcool : tu reportes',
      ],
},
    {
      type: 'story',
      icon: Quote,
      title: 'Olivier Razemon, journaliste mobilités — Le Monde · chronique « L’interconnexion n’est plus assurée »',
      body: '« Quand on dit « c’était pas de chance », on s’autorise à ne rien changer. Quand on dit « j’ai contribué à cette situation », on accepte de changer. C’est tout le travail de prévention. »',
    },
    {
      type: 'tip',
      icon: Users,
      title: 'En association ou en club moto ?',
      body: 'Partage cette enquête avant ton prochain run de groupe. <strong>3 vies sauvées par an</strong> si chaque motard de France retire 1 facteur de risque de sa pratique. Ce n’est pas du fatalisme. C’est de l’arithmétique.',
    }
  ],
};

export default fataliteAccidentEnquete;
